import { Component, NgModule, OnInit, Input, Output, EventEmitter, HostListener, ChangeDetectorRef, ViewChild } from '@angular/core';
import { MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';
import { MdDialog, MdDialogConfig } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormArray } from '@angular/forms/src/model';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/primeng';

import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';

import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { GlobalValuesService } from 'app/services/global-values.service';
import { ConstantsService } from 'app/services/constants-service';
import { ArchivalConfirmComponent } from './archivalconfirm/archival-confirm.component';
import { unsavedFormCheck } from 'app/shared/validators/generic-validators';
import { ArchivalCandidate } from './archival-candidate.model';

import { ArchivalService } from './archival.service';

@Component({
    selector: 'process-archival',
    providers: [ArchivalService, ConfirmationService],
    templateUrl: './archival.component.html',
    styleUrls: ['./archival.component.css']
})

/**
 * Archival component to for entity deletion.
 *
 * @author mohit.khurana@vernuso.com (Mohit Khurana)
 */
export class ArchivalComponent implements OnInit {
    dialogRef: MdDialogRef<any>;
    selectedIndex: 0;
    subscription: any;
    entityName: string;
    entityElementsToDelete: FormArray;
    archivalEntityListItems: any[] = [];
    archivalEntitySearchURI = ConstantsService.ARCHIVAL_ENTITY_SEARCH;
    archivalEntitySearchPlaceHolder = MessageService.ARCHIVAL_ENTITY_SEARCH_PLACEHOLDER;
    findWithoutRelationsLabel = MessageService.ARCHIVAL_FIND_WITHOUT_RELATIONS;
    withScreeningHitsOnlyLabel = MessageService.ARCHIVAL_WITH_SCREENING_HITS_ONLY;
    withNoDocumentsOnlyLabel = MessageService.ARCHIVAL_WITH_NO_DOCUMENTS_ONLY;
    addAllToListLabel = MessageService.ARCHIVAL_ADD_ALL_TO_LIST;
    orphanEntities: ArchivalCandidate[] = [];
    withScreeningHitsOnly = false;
    withNoDocumentsOnly = false;
    loadingOrphanEntities = false;
    orphanEntitiesLoaded = false;
    config: MdDialogConfig = {
        disableClose: true,
        width: '',
        height: '',
        position: {
            top: '',
            bottom: '',
            left: '',
            right: ''
        }
    };

    constructor(
        private formBuilder: FormBuilder,
        private archivalService: ArchivalService,
        private warmUpService: WarmUpService,
        private cdr: ChangeDetectorRef,
        private dialog: MdDialog,
        private snackBarService: SnackBarService,
        private globalValuesService: GlobalValuesService,
        private router: Router,
        private trackingService: TrackingService,
        private confirmationService: ConfirmationService,) { }

    ngOnInit(): void {
        // Nothing as of now. It will be updated later.
    }

    buildForm() {
        // Nothing as of now. It will be updated later.
    }

    selectedIndexChange(tabIndex) {
        this.selectedIndex = tabIndex;
    }

    findEntitiesWithoutRelations() {
        this.loadingOrphanEntities = true;
        this.orphanEntitiesLoaded = false;
        this.archivalService.findEntitiesWithoutRelations(
            this.withScreeningHitsOnly, this.withNoDocumentsOnly)
            .subscribe(response => {
                this.orphanEntities = response.arrResult || [];
                this.loadingOrphanEntities = false;
                this.orphanEntitiesLoaded = true;
            },
            err => {
                this.loadingOrphanEntities = false;
                this.orphanEntitiesLoaded = true;
                this.orphanEntities = [];
                this.callSnackBarService(err, '', MessageService.ARCHIVAL_WITHOUT_RELATIONS_FAILURE);
            });
    }

    addOrphanEntityToList(candidate: ArchivalCandidate) {
        this.onEntitySelectionEvent(this.buildSearchDetailsFromCandidate(candidate));
    }

    addAllOrphanEntitiesToList() {
        for (const candidate of this.orphanEntities) {
            this.addOrphanEntityToList(candidate);
        }
    }

    hasScreeningHits(candidate: ArchivalCandidate): boolean {
        return candidate.sanctionHitCount > 0
            || candidate.pepHitCount > 0
            || candidate.badPressHitCount > 0
            || candidate.leakPapersHitCount > 0;
    }

    getScreeningHitSummary(candidate: ArchivalCandidate): string {
        const parts = [];
        if (candidate.sanctionHitCount > 0) {
            parts.push('Sanction: ' + candidate.sanctionHitCount);
        }
        if (candidate.pepHitCount > 0) {
            parts.push('PEP: ' + candidate.pepHitCount);
        }
        if (candidate.badPressHitCount > 0) {
            parts.push('Bad press: ' + candidate.badPressHitCount);
        }
        if (candidate.leakPapersHitCount > 0) {
            parts.push('Leak papers: ' + candidate.leakPapersHitCount);
        }
        return parts.join(', ');
    }

    getEntityProfileLogo(candidate: ArchivalCandidate): string {
        if (candidate.logo) {
            return candidate.logo;
        }
        if (candidate.entityType && candidate.entityType.toLowerCase() === 'org') {
            return ConstantsService.DEFAULT_ORG_LOGO;
        }
        if (candidate.gender && candidate.gender.toLowerCase() === 'female') {
            return ConstantsService.DEFAULT_PERSON_FEMALE_LOGO;
        }
        return ConstantsService.DEFAULT_PERSON_MALE_LOGO;
    }

    private buildSearchDetailsFromCandidate(candidate: ArchivalCandidate): GenericSearchDetails {
        const searchDetails = new GenericSearchDetails();
        searchDetails.entityId = candidate.entityId;
        searchDetails.clientName = candidate.name;
        searchDetails.clientType = candidate.entityType;
        searchDetails.clientId = candidate.clientId;
        searchDetails.clientGender = candidate.gender;
        searchDetails.clientLogo = this.getEntityProfileLogo(candidate);
        return searchDetails;
    }

    // Perform this function if search entity is selected from search results.
    onEntitySelectionEvent(searchDetails: GenericSearchDetails) {
        //  Get the entity logo to be displayed or display the default one.
        let entityProfileLogo = '';
        if (searchDetails.clientLogo) {
            entityProfileLogo = searchDetails.clientLogo;
        } else {
            if (searchDetails.clientType && searchDetails.clientType.toLowerCase() === 'org')
            {
                entityProfileLogo = ConstantsService.DEFAULT_ORG_LOGO;
            }
            else
            {
                // If male person, display male icon otherwise display female icon.
                if (searchDetails.clientGender && searchDetails.clientGender.toLowerCase() === 'female') {
                    entityProfileLogo = ConstantsService.DEFAULT_PERSON_FEMALE_LOGO;
                } else {
                    entityProfileLogo = ConstantsService.DEFAULT_PERSON_MALE_LOGO;
                }
            }
        }
        // Get search index and add item to archival list only if its not there already.
        let searchIndex = this.getEntityItemSearchIndex(searchDetails.entityId);
        if(searchIndex == -1)
        {
            this.archivalEntityListItems.push(
            {
                'entityItemId': searchDetails.entityId,
                'entityItemName': searchDetails.clientName,
                'entityProfileLogo': entityProfileLogo,
                'entityClientType': searchDetails.clientType,
                'entityClientId': searchDetails.clientId,
            });
        }
    }

    // Get selected search item's index in archival list.
    getEntityItemSearchIndex(searchValue) {
        let returnEntityItemIndex = -1;
        for(var i = 0, len = this.archivalEntityListItems.length; i < len; i++) {
            if (this.archivalEntityListItems[i].entityItemId === searchValue)
            {
                returnEntityItemIndex = i;
            }
        }
        return returnEntityItemIndex;
    }

    // Method to open confirmation dialog for archival.
    openArchivalConfirmDialog(argEntityItemId?: number, argEntityClientType?: any, argClientId?: any) {
        // To check if all selected entities are going to be deleted in one go.
        let multipleArchival = (!argEntityItemId) ? true : false;
        this.dialogRef = this.dialog.open(ArchivalConfirmComponent, this.config);
        this.dialogRef.componentInstance.argMultipleArchival = multipleArchival;
        // If multiple archival is there, then pass all selected entity object array.
        if(multipleArchival) {
            this.dialogRef.componentInstance.argEntityValues = this.archivalEntityListItems;
        // else send only selected entity Id.
        } else {
            this.dialogRef.componentInstance.argEntityId = argEntityItemId;
            // If entity is an organisation and client Id is present, only in
            // that case, send client only flag as true otherwise send false
            // only.
            if (argEntityClientType && argEntityClientType.toLowerCase() === 'org' && argClientId) {
                this.dialogRef.componentInstance.argDeleteClientOnly = true;
            } else {
                this.dialogRef.componentInstance.argDeleteClientOnly = false;
            }
        }
        //@todo This code will change once REST is implemented.
        this.dialogRef.afterClosed().subscribe(result => {
            this.dialogRef = null;
            if (result) {
                this.callSnackBarService(result);
                if(argEntityItemId)
                {
                    this.archiveEntityItemFromList(false, argEntityItemId);
                }
                else
                {
                    this.archiveEntityItemFromList(false);
                }
                if (this.orphanEntitiesLoaded) {
                    this.findEntitiesWithoutRelations();
                }
            }
        });
    }

    // Method to display success / failure message.
    callSnackBarService(response: any, successMsg?: string, failureMsg?: string) {
        this.snackBarService
            .showStatus(response,
            successMsg || MessageService.ARCHIVAL_SUCCESS,
            failureMsg || MessageService.ARCHIVAL_FAILURE);
    }

    // Delete entry from archival list if selected to archive.
    archiveEntityItemFromList(flagConfirm?: boolean, argEntityItemId?: any) {
        if(argEntityItemId)
        {
            let searchIndex = this.getEntityItemSearchIndex(argEntityItemId);
            if(flagConfirm) {
                this.confirmationService.confirm({
                    message: "Are you sure you want to remove this item from the list?",
                    accept: () => {
                        if(searchIndex !== -1)
                        {
                            this.archivalEntityListItems.splice(searchIndex, 1);
                        }
                    }
                });
            } else {
                if(searchIndex !== -1)
                {
                    this.archivalEntityListItems.splice(searchIndex, 1);
                }
            }
        }
        else
        {
            if(flagConfirm) {
                this.confirmationService.confirm({
                    message: "Are you sure you want to clear the selected list?",
                    accept: () => {
                        this.archivalEntityListItems = [];
                    }
                });
            } else {
                this.archivalEntityListItems = [];
            }
        }
    }

    // This is function to track the results for ngFor trackby.
    reloadArchivalList(index, entityItem) {
        return index; // or item.entityItemId
    }

    @HostListener('keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        // @todo To be updated later.
        //unsavedFormCheck(event, this.dialogRef, this.orgAddForm);
    }
}



// WEBPACK FOOTER //
// ./src/app/admin/archival/archival.component.ts